// /admin/users — Platform admin only. Manage all users across tenants.
import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, X, Search, Pencil, ShieldOff, Check, Trash2, KeyRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import {
  useAdminUsers,
  useCreateAdminUser,
  usePatchAdminUser,
  useDeleteAdminUser,
  useAdminResetPassword,
  useTenants,
  useInviteUser,
  usePendingUsers,
} from "../api/sitroom";
import { MODULE_KEYS } from "../lib/moduleAccess";

const formatErrorDetail = (err) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d)))).join("; ");
  if (typeof detail === "string") return detail;
  return err?.message || "Request failed";
};

const ROLES = [
  { value: "platform_admin", label: "Platform Admin", color: "#E24B4A" },
  { value: "tenant_admin", label: "Tenant Admin", color: "#f3b14a" },
  { value: "escalation_manager", label: "Escalation Manager", color: "#185FA5" },
  { value: "responder", label: "Responder", color: "#9198b0" },
  { value: "observer", label: "Observer", color: "#6b7388" },
];

const roleStyle = (role) => ROLES.find((r) => r.value === role) || ROLES[3];

const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "??";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const isPlatform = me?.role === "platform_admin";
  const { data: users = [], isLoading } = useAdminUsers(isPlatform);
  const { data: pending = [] } = usePendingUsers(Boolean(me));
  const { data: tenants = [] } = useTenants();
  const createUser = useCreateAdminUser();
  const inviteUser = useInviteUser();
  const patchUser = usePatchAdminUser();
  const deleteUser = useDeleteAdminUser();
  const resetPw = useAdminResetPassword();

  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // user to delete
  const [confirmReset, setConfirmReset] = useState(null);   // user to reset password
  const [credentialsDialog, setCredentialsDialog] = useState(null); // {title, email, password}

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => (map[t.id] = t.name));
    return map;
  }, [tenants]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q) ||
        (tenantNameById[u.tenant_id] || "").toLowerCase().includes(q),
    );
  }, [users, search, tenantNameById]);

  if (!me) return null;
  if (me.role !== "platform_admin" && me.role !== "tenant_admin") {
    return <Navigate to="/" replace />;
  }

  const handleDeactivate = (u) => {
    patchUser.mutate(
      { id: u.id, patch: { is_active: !(u.is_active ?? true) } },
      {
        onSuccess: () =>
          addToast({
            type: "info",
            title: u.is_active ? "User deactivated" : "User reactivated",
            body: u.email,
          }),
      },
    );
  };

  const handleSaveEdit = (u, role, tenant_id) => {
    patchUser.mutate(
      { id: u.id, patch: { role, tenant_id: tenant_id || null } },
      {
        onSuccess: () => {
          addToast({ type: "info", title: "User updated", body: u.email });
          setEditingId(null);
        },
      },
    );
  };

  const handleDeleteConfirmed = (u) => {
    deleteUser.mutate(u.id, {
      onSuccess: () => {
        addToast({ type: "info", title: "User deleted", body: u.email });
        setConfirmDelete(null);
      },
      onError: (err) => {
        addToast({
          type: "info",
          title: "Delete failed",
          body: err?.response?.data?.detail || "Could not delete user",
        });
        setConfirmDelete(null);
      },
    });
  };

  // Iter81 — admin reset-password: manual password entry only. On success
  // open CredentialsDialog with email + password + Copy.
  const handleResetPasswordSubmit = (u, newPassword) => {
    resetPw.mutate(
      { id: u.id, body: { new_password: newPassword, force_change_on_next_login: true } },
      {
        onSuccess: (res) => {
          setConfirmReset(null);
          setCredentialsDialog({
            title: "Password reset",
            email: res.email || u.email,
            password: res.new_password || newPassword,
          });
        },
        onError: (err) => {
          addToast({
            type: "info",
            title: "Reset failed",
            body: formatErrorDetail(err),
          });
        },
      },
    );
  };

  return (
    <div className="p-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
        <div>
          <h1
            data-testid="view-title"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 28,
              fontWeight: 600,
              color: "#f0f2f8",
              letterSpacing: "-0.3px",
            }}
          >
            {isPlatform ? "User Management" : "Invite Users"}
          </h1>
          <div
            className="mt-1"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              color: "#f3b14a",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            {isPlatform
              ? `Platform Admin · All tenants · ${users.length} users · ${pending.length} pending`
              : `Tenant Admin · Invite-only (Platform Admin approves) · ${pending.length} pending`}
          </div>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="flex items-center gap-2 px-4 py-2 rounded transition-colors"
          style={{
            background: "#185FA5",
            border: "1px solid #185FA5",
            color: "#fff",
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
          data-testid="invite-user-btn"
        >
          <Plus size={14} /> {isPlatform ? "Create User" : "Invite User"}
        </button>
      </div>

      {/* Tenant admin: simplified view — invite-only + pending counter pointing to /admin/approvals */}
      {!isPlatform && (
        <div
          className="mt-5 rounded-md p-4"
          style={{ background: "#0d0f18", border: "1px dashed rgba(243,177,74,0.40)" }}
          data-testid="tenant-admin-invite-banner"
        >
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "#f0f2f8", marginBottom: 6 }}>
            Tenant admin · invite-only path
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
            Use the <b style={{ color: "#9ec3ec" }}>Invite User</b> button to add a teammate. They receive a
            <span style={{ color: "#f3b14a" }}> pending_approval </span> status until a platform admin approves
            them. Track pending invites under <b style={{ color: "#9ec3ec" }}>/admin/approvals</b>.
          </div>
          {pending.length > 0 && (
            <div className="mt-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f3b14a" }}>
              You have <b>{pending.length}</b> pending invitation{pending.length === 1 ? "" : "s"} awaiting platform admin review.
            </div>
          )}
        </div>
      )}

      {/* Search — platform admin only */}
      {isPlatform && (
      <div className="relative mt-5 mb-3" style={{ maxWidth: 360 }}>
        <Search
          size={13}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7388" }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, role, tenant…"
          className="w-full pl-7 pr-3 py-1.5 rounded outline-none"
          style={{
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#f0f2f8",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}
          data-testid="admin-users-search"
        />
      </div>
      )}

      {/* Table — platform admin only */}
      {isPlatform && (
      <div
        className="rounded-md overflow-hidden"
        style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div
          className="grid items-center px-4 py-2"
          style={{
            gridTemplateColumns: "1.4fr 1.4fr 1.3fr 1.3fr 110px 130px 170px",
            background: "rgba(255,255,255,0.03)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "#9198b0",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          <div>Name</div>
          <div>Email</div>
          <div>Role</div>
          <div>Tenant</div>
          <div>Status</div>
          <div>Last Login</div>
          <div>Actions</div>
        </div>
        {isLoading && (
          <div className="px-4 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>
            Loading users…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="px-4 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>
            No users match your search.
          </div>
        )}
        {filtered.map((u) => (
          <UserRow
            key={u.id}
            user={u}
            tenants={tenants}
            tenantName={tenantNameById[u.tenant_id]}
            editing={editingId === u.id}
            onEdit={() => setEditingId(u.id)}
            onCancelEdit={() => setEditingId(null)}
            onSave={(role, tenant_id) => handleSaveEdit(u, role, tenant_id)}
            onDeactivate={() => handleDeactivate(u)}
            onDelete={() => setConfirmDelete(u)}
            onResetPassword={() => setConfirmReset(u)}
            isSelf={me.id === u.id}
          />
        ))}
      </div>
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          user={confirmDelete}
          busy={deleteUser.isPending}
          onConfirm={() => handleDeleteConfirmed(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {confirmReset && (
        <ResetPasswordModal
          user={confirmReset}
          busy={resetPw.isPending}
          onConfirm={(pw) => handleResetPasswordSubmit(confirmReset, pw)}
          onCancel={() => setConfirmReset(null)}
        />
      )}

      {credentialsDialog && (
        <CredentialsDialog
          title={credentialsDialog.title}
          email={credentialsDialog.email}
          password={credentialsDialog.password}
          onClose={() => setCredentialsDialog(null)}
        />
      )}

      {showInvite && (
        <InviteUserModal
          tenants={tenants}
          isPlatform={isPlatform}
          forcedTenantId={isPlatform ? null : me.tenant_id}
          onClose={() => setShowInvite(false)}
          onSubmit={(body) => {
            if (isPlatform) {
              // Platform admin: direct create — user is active immediately
              createUser.mutate(body, {
                onSuccess: (created) => {
                  setShowInvite(false);
                  setCredentialsDialog({
                    title: "User created",
                    email: created.email,
                    password: created.initial_password || body.password,
                  });
                },
                onError: (err) =>
                  addToast({
                    type: "info",
                    title: "Create failed",
                    body: formatErrorDetail(err),
                  }),
              });
            } else {
              // Tenant admin: invite path — pending_approval until platform admin approves.
              // TenantInviteIn forbids extras → strip tenant_id (server stamps it from admin) + send_invite_email.
              const { send_invite_email, tenant_id, ...inviteBody } = body;
              inviteUser.mutate(inviteBody, {
                onSuccess: (created) => {
                  addToast({
                    type: "info",
                    title: "Invitation sent",
                    body: `${created.email} · pending platform admin approval`,
                  });
                  setShowInvite(false);
                },
                onError: (err) =>
                  addToast({
                    type: "info",
                    title: "Invite failed",
                    body: formatErrorDetail(err),
                  }),
              });
            }
          }}
        />
      )}
    </div>
  );
}

const UserRow = ({ user, tenants, tenantName, editing, onEdit, onCancelEdit, onSave, onDeactivate, onDelete, onResetPassword, isSelf }) => {
  const role = roleStyle(user.role);
  const [editRole, setEditRole] = useState(user.role);
  const [editTenant, setEditTenant] = useState(user.tenant_id || "");
  const active = user.is_active !== false;

  return (
    <div
      className="grid items-center px-4 py-2.5"
      style={{
        gridTemplateColumns: "1.4fr 1.4fr 1.3fr 1.3fr 110px 130px 170px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        fontFamily: "'DM Mono', monospace",
        fontSize: 11,
        color: "#c5cad8",
        opacity: active ? 1 : 0.5,
      }}
      data-testid={`admin-user-row-${user.id}`}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: 28,
            height: 28,
            background: "rgba(24,95,165,0.40)",
            border: "1px solid rgba(24,95,165,0.55)",
            color: "#cfe1f6",
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
          }}
        >
          {initials(user.name || user.email)}
        </span>
        <span style={{ color: "#f0f2f8" }}>{user.name || "—"}</span>
      </div>
      <div style={{ color: "#9ec3ec", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
      <div>
        {editing ? (
          <select
            value={editRole}
            onChange={(e) => setEditRole(e.target.value)}
            className="outline-none"
            style={{
              background: "#08090d",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              padding: "3px 6px",
              borderRadius: 4,
            }}
            data-testid={`admin-user-role-select-${user.id}`}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        ) : (
          <span
            className="inline-block px-2 py-0.5 rounded"
            style={{
              background: `${role.color}1f`,
              border: `1px solid ${role.color}55`,
              color: role.color,
              fontSize: 9,
              letterSpacing: "1.2px",
              textTransform: "uppercase",
            }}
            data-testid={`admin-user-role-${user.id}`}
          >
            {role.label}
          </span>
        )}
      </div>
      <div>
        {editing ? (
          <select
            value={editTenant}
            onChange={(e) => setEditTenant(e.target.value)}
            className="outline-none w-full"
            style={{
              background: "#08090d",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              padding: "3px 6px",
              borderRadius: 4,
            }}
          >
            <option value="">(no tenant — all)</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        ) : (
          <span style={{ color: tenantName ? "#c5cad8" : "#6b7388" }}>{tenantName || "All tenants"}</span>
        )}
      </div>
      <div>
        <span
          className="inline-block px-2 py-0.5 rounded"
          style={{
            background: active ? "rgba(78,208,132,0.10)" : "rgba(226,75,74,0.10)",
            border: active ? "1px solid rgba(78,208,132,0.35)" : "1px solid rgba(226,75,74,0.35)",
            color: active ? "#4ed084" : "#E24B4A",
            fontSize: 9,
            letterSpacing: "1.2px",
            textTransform: "uppercase",
          }}
        >
          {active ? "Active" : "Inactive"}
        </span>
      </div>
      <div style={{ color: "#9198b0" }}>{user.last_login || "—"}</div>
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <button
              onClick={() => onSave(editRole, editTenant)}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "rgba(78,208,132,0.10)",
                border: "1px solid rgba(78,208,132,0.35)",
                color: "#4ed084",
                cursor: "pointer",
                fontSize: 10,
                letterSpacing: "1px",
              }}
              data-testid={`admin-user-save-${user.id}`}
            >
              <Check size={11} />
            </button>
            <button
              onClick={onCancelEdit}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#9198b0",
                cursor: "pointer",
                fontSize: 10,
              }}
            >
              <X size={11} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onEdit}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#c5cad8",
                cursor: "pointer",
                fontSize: 10,
              }}
              data-testid={`admin-user-edit-${user.id}`}
              title="Edit"
            >
              <Pencil size={11} />
            </button>
            <button
              disabled={isSelf}
              onClick={onDeactivate}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "transparent",
                border: `1px solid ${isSelf ? "rgba(255,255,255,0.06)" : "rgba(226,75,74,0.35)"}`,
                color: isSelf ? "#3a3f52" : "#E24B4A",
                cursor: isSelf ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
              data-testid={`admin-user-deactivate-${user.id}`}
              title={isSelf ? "Cannot deactivate yourself" : (active ? "Deactivate" : "Reactivate")}
            >
              <ShieldOff size={11} />
            </button>
            <button
              onClick={onResetPassword}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "transparent",
                border: "1px solid rgba(243,177,74,0.35)",
                color: "#f3b14a",
                cursor: "pointer",
                fontSize: 10,
              }}
              data-testid={`admin-user-reset-password-${user.id}`}
              title="Reset password (forces change on next login)"
            >
              <KeyRound size={11} />
            </button>
            <button
              disabled={isSelf}
              onClick={onDelete}
              className="px-2 py-1 rounded transition-colors"
              style={{
                background: "transparent",
                border: `1px solid ${isSelf ? "rgba(255,255,255,0.06)" : "rgba(226,75,74,0.35)"}`,
                color: isSelf ? "#3a3f52" : "#E24B4A",
                cursor: isSelf ? "not-allowed" : "pointer",
                fontSize: 10,
              }}
              data-testid={`admin-user-delete-${user.id}`}
              title={isSelf ? "Cannot delete yourself" : "Delete user"}
            >
              <Trash2 size={11} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const InviteUserModal = ({ tenants, isPlatform = true, forcedTenantId = null, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    name: "", email: "", role: "responder", tenant_id: forcedTenantId || "", phone: "", send_invite_email: true,
    module_access: [], // Iter38 — [] = full access
    password: "",      // Iter81 — admin-typed initial password (platform path only)
  });
  const [errors, setErrors] = useState({});

  // Tenant admins cannot grant platform_admin role
  const availableRoles = isPlatform ? ROLES : ROLES.filter((r) => r.value !== "platform_admin");

  const toggleModule = (key) => {
    setForm((s) => ({
      ...s,
      module_access: s.module_access.includes(key)
        ? s.module_access.filter((k) => k !== key)
        : [...s.module_access, key],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    if (isPlatform) {
      if (!form.password.trim()) errs.password = "Required";
      else if (form.password.trim().length < 8) errs.password = "Min 8 characters";
    }
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      onSubmit({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        tenant_id: form.tenant_id || null,
        phone: form.phone.trim() || null,
        module_access: form.module_access.length ? form.module_access : null,
        password: isPlatform ? form.password.trim() : undefined,
      });
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[9990]"
        style={{ background: "rgba(8,9,13,0.65)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <div
        className="fixed top-1/2 left-1/2 z-[9991] -translate-x-1/2 -translate-y-1/2 rounded-lg"
        style={{
          width: "min(440px, 92vw)",
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
        }}
        data-testid="invite-user-modal"
      >
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16, color: "#f0f2f8" }}>
            {isPlatform ? "Create user" : "Invite user"}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded transition-colors"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#9198b0", cursor: "pointer" }}
            data-testid="invite-user-close"
          >
            <X size={14} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {[
            { key: "name", label: "Name", required: true },
            { key: "email", label: "Email", type: "email", required: true },
            { key: "phone", label: "Phone (optional)", placeholder: "+49 170…" },
          ].map((f) => (
            <div key={f.key}>
              <Label>{f.label}{f.required ? " *" : ""}</Label>
              <input
                type={f.type || "text"}
                value={form[f.key]}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder || ""}
                className="w-full outline-none"
                style={{
                  background: "#08090d",
                  border: `1px solid ${errors[f.key] ? "#E24B4A" : "rgba(255,255,255,0.10)"}`,
                  color: "#f0f2f8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  padding: "8px 10px",
                  borderRadius: 6,
                }}
                data-testid={`invite-${f.key}-input`}
              />
              {errors[f.key] && (
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#E24B4A", marginTop: 3 }}>
                  {errors[f.key]}
                </div>
              )}
            </div>
          ))}
          {isPlatform && (
            <div>
              <Label>Initial password *</Label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                placeholder="Min 8 characters"
                className="w-full outline-none"
                style={{
                  background: "#08090d",
                  border: `1px solid ${errors.password ? "#E24B4A" : "rgba(255,255,255,0.10)"}`,
                  color: "#f0f2f8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  padding: "8px 10px",
                  borderRadius: 6,
                }}
                data-testid="invite-password-input"
                autoComplete="new-password"
              />
              {errors.password ? (
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#E24B4A", marginTop: 3 }}>
                  {errors.password}
                </div>
              ) : (
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", marginTop: 3 }}>
                  User will be forced to change this on first login.
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Role *</Label>
            <select
              value={form.role}
              onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
              className="w-full outline-none"
              style={{
                background: "#08090d",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#f0f2f8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                padding: "8px 10px",
                borderRadius: 6,
              }}
              data-testid="invite-role-select"
            >
              {availableRoles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Tenant{!isPlatform ? " (locked)" : ""}</Label>
            {isPlatform ? (
              <select
                value={form.tenant_id}
                onChange={(e) => setForm((s) => ({ ...s, tenant_id: e.target.value }))}
                className="w-full outline-none"
                style={{
                  background: "#08090d",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#f0f2f8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  padding: "8px 10px",
                  borderRadius: 6,
                }}
                data-testid="invite-tenant-select"
              >
                <option value="">(no tenant — all tenants)</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={forcedTenantId || "—"}
                disabled
                className="w-full outline-none"
                style={{
                  background: "#08090d",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#9ec3ec",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  padding: "8px 10px",
                  borderRadius: 6,
                  opacity: 0.85,
                }}
                data-testid="invite-tenant-locked"
              />
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }}>
            <input
              type="checkbox"
              checked={form.send_invite_email}
              onChange={(e) => setForm((s) => ({ ...s, send_invite_email: e.target.checked }))}
              data-testid="invite-send-email"
            />
            Send invite email
          </label>

          {/* Iter38 — module_access multi-select (empty = full access) */}
          <div data-testid="invite-module-access">
            <div className="mb-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>
              Module access ({form.module_access.length === 0 ? "ALL" : `${form.module_access.length} selected`})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {MODULE_KEYS.map((m) => {
                const on = form.module_access.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleModule(m)}
                    className="px-2 py-0.5 rounded transition-colors"
                    style={{
                      background: on ? "rgba(24,95,165,0.18)" : "transparent",
                      border: `1px solid ${on ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                      color: on ? "#9ec3ec" : "#9aa1b3",
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 9,
                      letterSpacing: "0.3px",
                      cursor: "pointer",
                    }}
                    data-testid={`invite-module-${m}`}
                    aria-pressed={on}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            <div className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>
              Leave empty for full role-based access. Pick one or more to restrict this user.
            </div>
          </div>
          <div
            className="rounded px-3 py-2"
            style={{
              background: isPlatform ? "rgba(243,177,74,0.08)" : "rgba(24,95,165,0.10)",
              border: `1px solid ${isPlatform ? "rgba(243,177,74,0.30)" : "rgba(24,95,165,0.35)"}`,
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              color: isPlatform ? "#f3b14a" : "#9ec3ec",
              letterSpacing: "0.3px",
            }}
          >
            {isPlatform
              ? <>Temporary password: <span style={{ color: "#fff" }}>sitroom2024</span> · User is <b>active immediately</b></>
              : <>User will be <b>pending_approval</b> until a platform admin approves them. They cannot sign in before approval.</>}
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 rounded transition-colors"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#c5cad8",
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                letterSpacing: "1px",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-3 py-2 rounded transition-colors"
              style={{
                background: "#185FA5",
                border: "1px solid #185FA5",
                color: "#fff",
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
              data-testid="invite-submit-btn"
            >
              {isPlatform ? "Create" : "Invite"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const Label = ({ children }) => (
  <div
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 10,
      color: "#9198b0",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const ConfirmDeleteModal = ({ user, busy, onConfirm, onCancel }) => (
  <>
    <div
      className="fixed inset-0 z-[9990]"
      style={{ background: "rgba(8,9,13,0.65)", backdropFilter: "blur(4px)" }}
      onClick={busy ? undefined : onCancel}
    />
    <div
      className="fixed top-1/2 left-1/2 z-[9991] -translate-x-1/2 -translate-y-1/2 rounded-lg"
      style={{
        width: "min(420px, 92vw)",
        background: "#0d0f18",
        border: "1px solid rgba(226,75,74,0.35)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
      }}
      data-testid="confirm-delete-user-modal"
    >
      <div className="px-5 py-4">
        <h2
          style={{
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 16,
            color: "#E24B4A",
            marginBottom: 8,
          }}
        >
          Delete user?
        </h2>
        <p
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
            color: "#c5cad8",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          This deactivates <span style={{ color: "#9ec3ec" }}>{user.email}</span> and
          revokes access. Audit history and authored content are preserved. You can
          reactivate later via the user list.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#c5cad8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              letterSpacing: "1px",
              textTransform: "uppercase",
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="confirm-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded transition-colors"
            style={{
              background: "rgba(226,75,74,0.15)",
              border: "1px solid rgba(226,75,74,0.55)",
              color: "#E24B4A",
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 12,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="confirm-delete-confirm"
          >
            {busy ? "Deleting…" : "Delete user"}
          </button>
        </div>
      </div>
    </div>
  </>
);

const ResetPasswordModal = ({ user, busy, onConfirm, onCancel }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    const v = pw.trim();
    if (v.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setErr("");
    onConfirm(v);
  };
  return (
    <>
      <div
        className="fixed inset-0 z-[9990]"
        style={{ background: "rgba(8,9,13,0.65)", backdropFilter: "blur(4px)" }}
        onClick={busy ? undefined : onCancel}
      />
      <div
        className="fixed top-1/2 left-1/2 z-[9991] -translate-x-1/2 -translate-y-1/2 rounded-lg"
        style={{
          width: "min(460px, 92vw)",
          background: "#0d0f18",
          border: "1px solid rgba(243,177,74,0.35)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
        }}
        data-testid="reset-password-modal"
      >
        <div className="px-5 py-4">
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16, color: "#f3b14a", marginBottom: 8 }}>
            Reset password
          </h2>
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9aa1b3", marginBottom: 10 }}>
            User: <span style={{ color: "#9ec3ec" }}>{user.email}</span>
          </p>
          <Label>New password *</Label>
          <input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Min 8 characters"
            autoFocus
            autoComplete="new-password"
            className="w-full outline-none mb-1"
            style={{
              background: "#08090d",
              border: `1px solid ${err ? "#E24B4A" : "rgba(255,255,255,0.10)"}`,
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              padding: "8px 10px",
              borderRadius: 6,
            }}
            data-testid="reset-password-input"
          />
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: err ? "#E24B4A" : "#9198b0", marginBottom: 14 }}>
            {err || "User will be forced to change this on next login."}
          </div>
          <div className="flex gap-2">
            <button
              type="button" onClick={onCancel} disabled={busy}
              className="flex-1 px-3 py-2 rounded transition-colors"
              style={{
                background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
                color: "#c5cad8", fontFamily: "'DM Mono', monospace", fontSize: 11,
                letterSpacing: "1px", textTransform: "uppercase",
                cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
              data-testid="reset-password-cancel"
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={busy}
              className="flex-1 px-3 py-2 rounded transition-colors"
              style={{
                background: "rgba(243,177,74,0.15)", border: "1px solid rgba(243,177,74,0.55)",
                color: "#f3b14a", fontFamily: "Syne, sans-serif", fontWeight: 600,
                fontSize: 12, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
              }}
              data-testid="reset-password-submit"
            >
              {busy ? "Resetting…" : "Reset password"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const CredentialsDialog = ({ title, email, password, onClose }) => {
  const [copied, setCopied] = useState(false);
  const text = `Email: ${email}\nPassword: ${password}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Fallback: create a textarea so the admin can still copy manually.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* ignore */ }
      ta.remove();
    }
  };
  return (
    <>
      <div
        className="fixed inset-0 z-[9992]"
        style={{ background: "rgba(8,9,13,0.70)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />
      <div
        className="fixed top-1/2 left-1/2 z-[9993] -translate-x-1/2 -translate-y-1/2 rounded-lg"
        style={{
          width: "min(520px, 94vw)",
          background: "#0d0f18",
          border: "1px solid rgba(78,208,132,0.35)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
        }}
        data-testid="credentials-dialog"
      >
        <div className="px-5 py-4">
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 16, color: "#4ed084", marginBottom: 8 }}>
            {title}
          </h2>
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.55, marginBottom: 12 }}>
            Send these credentials to the user. They will be required to change
            the password on first login.
          </p>
          <div
            className="rounded p-3 mb-3"
            style={{
              background: "#08090d",
              border: "1px solid rgba(255,255,255,0.10)",
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              color: "#f0f2f8",
              whiteSpace: "pre",
              userSelect: "all",
            }}
            data-testid="credentials-block"
          >
            <div>Email:    <span style={{ color: "#9ec3ec" }} data-testid="credentials-email">{email}</span></div>
            <div>Password: <span style={{ color: "#9aebc2" }} data-testid="credentials-password">{password}</span></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button" onClick={copy}
              className="px-3 py-2 rounded transition-colors"
              style={{
                background: copied ? "rgba(78,208,132,0.20)" : "rgba(24,95,165,0.16)",
                border: `1px solid ${copied ? "rgba(78,208,132,0.55)" : "rgba(24,95,165,0.55)"}`,
                color: copied ? "#9aebc2" : "#9ec3ec",
                fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700,
                letterSpacing: "0.5px", cursor: "pointer",
              }}
              data-testid="credentials-copy"
            >
              {copied ? "✓ COPIED" : "📋 COPY"}
            </button>
            <button
              type="button" onClick={onClose}
              className="px-3 py-2 rounded transition-colors"
              style={{
                background: "transparent", border: "1px solid rgba(255,255,255,0.14)",
                color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600,
                fontSize: 12, cursor: "pointer",
              }}
              data-testid="credentials-close"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

